import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { useCurrentUser } from '../../../UserProvider';
import { api, setAuthToken } from '../../../utils/api';

const schema = z.object({
  password: z.string()
    .nonempty('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(/\d/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  passwordConfirmation: z.string().nonempty('Password confirmation is required')
}).refine((data) => data.password === data.passwordConfirmation, {
  message: 'Passwords do not match',
  path: ['passwordConfirmation'],
});

function createInitialValues() {
  return {
    password: '',
    passwordConfirmation: ''
  };
}

function createInitialErrors() {
  return {
    password: null,
    passwordConfirmation: null
  };
}

export default function PasswordResetStart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useCurrentUser();

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      const { data } = await api.put('/password_resets/finalize', {
        email: decodeURIComponent(searchParams.get('em')),
        code: searchParams.get('cd'),
        password: result.data.password
      });

      setAuthToken(data);

      await refreshUser();

      navigate('/jobs');

    } catch {
      setError('Something went wrong, please try again.');

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Create a new password
        </h1>
        <p className='form__subtitle'>
          Your new password must be different from your previous password
        </p>
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='password' className='text-field__label'>
              Password
            </label>
            <input
              id='password'
              name='password'
              type='password'
              placeholder='• • • • • • • •'
              value={values.password}
              required
              onChange={(e) => setFieldValue('password', e.target.value)}
              className='text-field__input'
            />
            {errors.password && (
              <p className='text-field__error'>
                {errors.password}
              </p>
            )}
          </div>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='password_confirmation' className='text-field__label'>
              Confirm Password
            </label>
            <input
              id='password_confirmation'
              name='password_confirmation'
              type='password'
              placeholder='• • • • • • • •'
              value={values.passwordConfirmation}
              required
              onChange={(e) => setFieldValue('passwordConfirmation', e.target.value)}
              className='text-field__input'
            />
            {errors.passwordConfirmation && (
              <p className='text-field__error'>
                {errors.passwordConfirmation}
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <div className='form__button-group'>
          <Link className='button button--white button--outline' to='/password-reset' reloadDocument>
            Try again
          </Link>
          <button type='submit' className='button button--white button--fill'>
            {isLoading ? 'Loading...' : 'Reset Password'}
          </button>
        </div>
      </form>
    </Fragment>
  );
}